/* ==========================================================================
   HK Layout — global behaviour moved out of theme.liquid
   Loaded with `defer` from <head>, so the DOM is parsed when this runs.
   Modules:
     1. Catalog button alignment (desktop): CSS since UX-045
     2. Header hide while homepage catalog slideshow is in view (desktop)
     3. Header dropdown / flyout behaviour
     4. Cart count bubble bounce
     5. iOS scroll lock (menu drawer)
     6. Lead user_data for ad measurement (consent-gated)
   ========================================================================== */
(() => {
  'use strict';

  const html = document.documentElement;
  const DESKTOP = window.matchMedia('(min-width: 990px)');


  const onSectionChange = (fn) => {
    ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:unload']
      .forEach((ev) => document.addEventListener(ev, fn));
  };

  const rafThrottle = (fn) => {
    let raf = 0;
    return () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; fn(); });
    };
  };


  /* ------------------------------------------------------------------------
     1. Catalog button alignment: pure CSS now (UX-045). #hk-critical in
     layout/theme.liquid derives the button's x and width from the viewport,
     so it no longer depends on a value cached after a homepage visit.
     ------------------------------------------------------------------------ */


  /* ------------------------------------------------------------------------
     2. Header hide while the homepage catalog slideshow is visible (desktop)
     Stage 1 (hk-header-stub): header collapses to a thin stub.
     Stage 2 (hk-no-header-reveal): header slides fully out.
     CSS lives in #hk-critical in <head>.
     NOTE: set Dawn's "Sticky header" setting to "Always" or "None" so Dawn's
     own on-scroll-up hide doesn't fight this.
     ------------------------------------------------------------------------ */
  (function headerHide() {
    const REF_SEL = '.hk-catsl[data-hk-catsl="1"]';
    const HEADER_SEL = 'sticky-header.header-wrapper, sticky-header, .header-wrapper';
    const ref = document.querySelector(REF_SEL);
    if (!ref) return; // only pages that have the catalog slideshow

    let refVisible = false;
    let headerH = 0;
    let state = '';

    function measureHeader() {
      if (state) return; // only measure while the header is full size
      const h = document.querySelector(HEADER_SEL);
      headerH = h ? h.offsetHeight : 90;
    }

    function apply() {
      const y = window.scrollY || 0;
      if (!headerH) measureHeader();

      let next = '';
      if (DESKTOP.matches && refVisible) {
        if (y >= Math.round(headerH + 40)) next = 'hk-no-header-reveal';
        else if (y >= Math.round(headerH * 0.6)) next = 'hk-header-stub';
      }

      if (next === state) return;
      html.classList.remove('hk-header-stub', 'hk-no-header-reveal');
      if (next) html.classList.add(next);
      state = next;
    }

    const schedule = rafThrottle(apply);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        const e = entries[0];
        const v = !!(e && e.isIntersecting);
        if (v !== refVisible) { refVisible = v; schedule(); }
      }, { threshold: [0, 0.01] }).observe(ref);
    } else {
      const check = () => {
        const r = ref.getBoundingClientRect();
        const v = r.bottom > 0 && r.top < window.innerHeight;
        if (v !== refVisible) { refVisible = v; schedule(); }
      };
      window.addEventListener('scroll', check, { passive: true });
      window.addEventListener('resize', check);
      check();
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', () => { if (!state) headerH = 0; schedule(); });
    window.addEventListener('orientationchange', schedule);
    onSectionChange(schedule);

    schedule();
  })();


  /* ------------------------------------------------------------------------
     3. Header dropdowns / flyouts
     - Opening a top-level menu closes the other top-level menus
     - Opening a flyout closes its sibling flyouts
     - Closing any menu closes everything nested inside it
     - Clicking outside the inline menu closes all of them
     The catalog mega-menu (.hk-hdr-cat__details) manages itself and is skipped.
     ------------------------------------------------------------------------ */
  (function headerMenus() {
    const MENU = '.header__inline-menu';
    // Everything inside the catalog mega-menu is managed by the catalog's own code
    const CATALOG = '.hk-hdr-cat__details, .hk-topnav-li--catalog';
    // Dawn wraps dropdowns in <header-menu>; older/custom markup may not
    const TOP = `${MENU} > ul > li > details, ${MENU} > ul > li > header-menu > details`;

    const close = (d) => d.removeAttribute('open');
    const inCatalog = (d) => !!d.closest(CATALOG);
    const isTopLevel = (d) => d.matches(TOP);
    const openIn = (root) =>
      Array.from(root.querySelectorAll('details[open]')).filter((d) => !inCatalog(d));

    document.addEventListener('click', (e) => {
      const menu = e.target.closest(MENU);

      // Click outside the menu → close every dropdown/flyout
      if (!menu) {
        document.querySelectorAll(MENU).forEach((m) => openIn(m).forEach(close));
        return;
      }

      const summary = e.target.closest('summary');
      if (!summary || !menu.contains(summary)) return;

      const details = summary.parentElement;
      if (!(details instanceof HTMLDetailsElement) || inCatalog(details)) return;

      if (isTopLevel(details)) {
        // Opening a top-level menu closes the other top-level menus
        openIn(menu).forEach((d) => {
          if (d !== details && isTopLevel(d)) close(d);
        });
      } else {
        // Opening a flyout closes its sibling flyouts in the same panel
        const panel = details.parentElement.closest('.header__submenu');
        if (!panel) return;
        openIn(panel).forEach((d) => {
          if (d !== details && !d.contains(details)) close(d);
        });
      }
    });

    // Closing any menu closes everything nested inside it.
    // 'toggle' doesn't bubble — listen in the capture phase.
    document.addEventListener('toggle', (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || d.open) return;
      if (!d.closest(MENU) || inCatalog(d)) return;
      d.querySelectorAll('details[open]').forEach(close);
    }, true);
  })();


  /* ------------------------------------------------------------------------
     4. Cart count bubble bounce
     Dawn re-renders #cart-icon-bubble with innerHTML on every cart update,
     which REPLACES the bubble element (and on 0 → 1 item creates it from
     nothing). So instead of watching one element, we track the count itself:
     whenever the header changes, re-read whatever bubble exists now and
     bounce it if the count differs from the last one we saw.
     ------------------------------------------------------------------------ */
  (function cartBubble() {
    const SEL = '.cart-count-bubble';
    const CLS = 'animating';
    const MS = 600;

    const readCount = () => {
      const b = document.querySelector(SEL);
      return b ? (b.textContent || '').trim() : '';
    };

    let last = readCount();

    function bounce(b) {
      b.classList.remove(CLS);
      void b.offsetWidth; // restart the animation if it's already running
      b.classList.add(CLS);
      clearTimeout(b._hkBounceT);
      b._hkBounceT = setTimeout(() => b.classList.remove(CLS), MS);
    }

    function check() {
      const now = readCount();
      if (now === last) return;
      last = now;
      const b = document.querySelector(SEL);
      if (b) bounce(b);
    }

    const root = document.querySelector('.header-wrapper') || document.querySelector('header') || document.body;
    new MutationObserver(check).observe(root, { childList: true, subtree: true, characterData: true });
  })();


  /* ------------------------------------------------------------------------
     5. iOS scroll lock
     Dawn adds .overflow-hidden-tablet to <body> when the menu drawer opens, and
     .overflow-hidden when the header search opens. hk-layout.css turns both into
     position:fixed below 990 px (the only lock iOS respects: <html> has
     overflow-x: clip, so body's overflow: hidden never reaches the viewport);
     here we pin the current scroll offset and restore it on close.
     ------------------------------------------------------------------------ */
  (function scrollLock() {
    const b = document.body;
    const phone = window.matchMedia('(max-width: 989px)');
    const searchOpen = () =>
      phone.matches && b.classList.contains('overflow-hidden') && !!document.querySelector('details-modal.header__search > details[open]');
    let savedY = 0;
    let tapY = null;

    /* Read the offset before Dawn locks <body> (capture phase of the drawer / search summary click). Reading
       scrollY after the class flip forced a full-page style + layout pass inside the tap (PERF-008). */
    document.addEventListener('click', (e) => {
      const s = e.target.closest && e.target.closest(phone.matches ? 'header-drawer summary, details-modal.header__search summary' : 'header-drawer summary');
      if (s && !b.hasAttribute('data-dawn-locked')) tapY = window.scrollY || window.pageYOffset || 0;
    }, { capture: true, passive: true });

    new MutationObserver(() => {
      const locked = b.classList.contains('overflow-hidden-tablet') || searchOpen();

      if (locked && !b.hasAttribute('data-dawn-locked')) {
        savedY = tapY !== null ? tapY : (window.scrollY || window.pageYOffset || 0);
        tapY = null;
        b.setAttribute('data-dawn-locked', '1');
        b.style.top = '-' + savedY + 'px';
      } else if (!locked && b.hasAttribute('data-dawn-locked')) {
        b.removeAttribute('data-dawn-locked');
        b.style.top = '';
        // a jump, not base.css's smooth scroll: an animated 0 → savedY read as "scrolling down" and hid the sticky header
        const html = document.documentElement;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, savedY);
        html.style.scrollBehavior = '';
      }
    }).observe(b, { attributes: true, attributeFilter: ['class'] });
  })();

  /* ── 6. Lead user_data (contact + rental forms) ─────────────────────────
     Email / phone for Google enhanced conversions only after the visitor
     allowed marketing in the cookie banner (window.ccConsent). Returns
     { email, phone_number } (phone in E.164) or null — callers then send the
     lead event without personal data. */
  window.hkLeadUserData = (email, phone) => {
    const c = window.ccConsent;
    if (!c || c.marketing !== true) return null;
    const e = String(email || '').trim().toLowerCase() || undefined;
    // E.164 from the shared phone rule (assets/hk-validate.js, ARCH-023)
    const p = window.HKValidate ? window.HKValidate.phone(phone).e164 : undefined;
    if (!e && !p) return null;
    return { email: e, phone_number: p };
  };
})();